<template>
  <eg-b-card class="px-2 px-md-4 py-1">
    <div class="text-center">
      <img
        class="mr-2"
        src="../../../assets/icons/canvas-full.svg"
        alt="Canvas"
      />
      <div class="mt-4 mb-3 title">{{ $t("imports.allowAccessToCanvas") }}</div>
    </div>

    <eg-b-alert class="default_color" variant="default" show>
      {{ $t("imports.needSomeHelp") }}
      <a
        href="https://egcdn.nyc3.cdn.digitaloceanspaces.com/videos/EssayGrader_CanvasTokenCreation.mp4"
        target="_blank"
        >{{ $t("imports.watchDemoVideo") }}</a
      >
    </eg-b-alert>

    <eg-additional-collapse
      class="canvas_collapse"
      :label="$t('imports.stepOneCanvasUrl')"
      accordion="accordion"
      :value="true"
    >
      <ul class="mb-0">
        <li>
          {{ $t("imports.canvasStep1Before") }}
          <b>{{ $t("imports.logIntoCanvas") }}</b>
          {{ $t("imports.canvasStep1Copy") }}
          <b>{{ $t("imports.canvasStep1Copy") }}</b>
          {{ $t("imports.canvasStep1After") }}
          <img
            class="canvas_instruction_img"
            src="../../../assets/illustrations/canvas-instruction.jpg"
            alt="Canvas Instruction"
          />
        </li>
        <li>
          <b>{{ $t("imports.canvasPaste") }}</b>
          {{ $t("imports.pasteUrlBelow") }}
        </li>
      </ul>

      <eg-form-group :label="$t('imports.canvasUrlLabel')">
        <eg-b-form-input
          v-model="canvasUrl"
          type="text"
          :placeholder="$t('imports.canvasUrlPlaceholder')"
          :state="canvasURLState"
        />
        <eg-b-form-text v-if="canvasURLState === null">
          {{ $t("imports.canvasUrlExample") }}
        </eg-b-form-text>
        <eg-b-form-valid-feedback v-if="canvasURLState">
          {{ $t("imports.looksGood") }}
        </eg-b-form-valid-feedback>
      </eg-form-group>
    </eg-additional-collapse>
    <eg-additional-collapse
      class="canvas_collapse mt-3"
      :label="$t('imports.stepTwoAccessToken')"
      accordion="accordion"
    >
      <ul class="mb-0">
        <li>
          {{ $t("imports.inAccountSettings") }}
          <b>{{ $t("imports.accountSettings") }}</b
          >{{ $t("imports.createAccessToken") }}
          <b>{{ $t("imports.canvasNewAccessToken") }}</b>
        </li>

        <li>
          {{ $t("imports.inPurposeField") }}
          <b>{{ $t("imports.purposeFieldName") }}</b>
          {{ $t("imports.addEssayGrader") }}
        </li>
        <li>
          {{ $t("imports.leaveExpiresBlank") }}
          <b>{{ $t("imports.expiresField") }}</b>
          {{ $t("imports.leaveExpiresBlankEnd") }}
        </li>
        <li>
          <b>{{ $t("common.generate") }}</b>
          {{ $t("imports.generateAndCopyToken") }}
        </li>
        <li>
          <b>{{ $t("imports.canvasPaste") }}</b>
          {{ $t("imports.pasteTokenBelow") }}
        </li>
      </ul>
      <eg-form-group :label="$t('imports.accessTokenLabel')">
        <eg-b-form-input
          v-model="canvasToken"
          type="text"
          :placeholder="$t('imports.pasteAccessTokenHere')"
          :state="canvasTokenState"
        />
        <eg-b-form-text v-if="canvasTokenState === null">
          {{ $t("imports.requiredField") }}
        </eg-b-form-text>
        <eg-b-form-valid-feedback v-if="canvasTokenState">
          {{ $t("imports.looksGood") }}
        </eg-b-form-valid-feedback>
      </eg-form-group>
    </eg-additional-collapse>

    <div class="text-right mt-2">
      <slot name="cancel-button"></slot>
      <slot
        name="continue-button"
        :canvas-url="canvasUrl"
        :canvas-token="canvasToken"
        :disabled="!canvasURLState || !canvasTokenState"
      >
        <eg-b-button
          variant="info"
          :disabled="!canvasURLState || !canvasTokenState"
          @click="importDetails(canvasUrl, canvasToken)"
        >
          {{ $t("common.continue") }}
        </eg-b-button>
      </slot>
    </div>
  </eg-b-card>
</template>

<script>
import isURL from "validator/es/lib/isURL";
import { getUser } from "@/api";
import importApi from "@/api/requests/import";

import EgAdditionalCollapse from "@/components/global/eg_additional_collapse.vue";
import EgFormGroup from "@/components/global/eg_form_group.vue";
import { mergeUserData } from "@/utils/userUtils";

export default {
  components: {
    EgAdditionalCollapse,
    EgFormGroup,
  },

  data() {
    return {
      canvasUrl: "",
      canvasToken: "",
    };
  },

  computed: {
    canvasURLState() {
      return isURL(this.canvasUrl) ? true : null;
    },

    canvasTokenState() {
      return this.canvasToken.trim().length > 0 ? true : null;
    },
  },

  methods: {
    async importDetails(canvasUrl, canvasToken) {
      try {
        const { data } = await importApi["canvas"].createCredentials(
          canvasUrl,
          canvasToken
        );
        this.serviceAccount.id = data.id;
        const { data: userData } = await getUser("me");
        const mergedUserData = mergeUserData(this, userData);
        await this.$store.dispatch("setUser", mergedUserData);

        if (userData.canvasPersonalCredentials || userData.cid) {
          this.$emit("updated");
        } else {
          this.$showToastError(this.$t("imports.pleaseCheckUrlAndToken"));
        }
      } catch (error) {
        this.$showToastError(this.$t("imports.pleaseCheckUrlAndToken"));
      }
    },
  },
};
</script>

<style scoped lang="scss">
.title {
  color: var(--Text-Body, #212529);
  font-family: "Inter";
  font-size: 32px;
  font-weight: 500;
  line-height: 38px;
}

.default_color {
  background: #d1ecf1;
}
.canvas_instruction_img {
  width: 243px;
  transform: translateX(-3px);
}

.canvas_collapse {
  font-family: "Inter";

  &::v-deep {
    .btn {
      color: var(--Text-Primary, #007bff);
      font-size: 20px;
      font-weight: 500;
      line-height: 24px;
    }
  }
}
</style>
